import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import FilterTag from '@components/atoms/filter-tag/filterTag';
import { ArrowRight } from '@components/icons/arrow/arrow';
import StyledShapedStage from './shapedStage.style';
import Picture from '../../atoms/picture/picture';
import Container from '../../layout/container/container';
import Headline from '../../atoms/headline/headline';

const ShapedStage = ({ anchorNav = [], titleImage, data }) => {
  const t = useTranslations();
  const desktopVideoRef = useRef();
  const mobileVideoRef = useRef();
  const [isPlaying, setIsPlaying] = useState(true);

  const handlePlay = () => {
    if (isPlaying) {
      desktopVideoRef.current.pause();
      mobileVideoRef.current.pause();
      setIsPlaying(false);
    } else {
      desktopVideoRef.current.play();
      mobileVideoRef.current.play();
      setIsPlaying(true);
    }
  };

  const smoothScrollToElementById = elementId => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const mobileVideo = data.mobileVideo?.[0]?.url;
  const video = data.video?.[0]?.url;

  return (
    <StyledShapedStage aria-label={data.headline || 'Hero banner'}>
      <div className="campaignStage__wrapper">
        {(titleImage || video) && (
          <div className="campaignStage__media">
            {titleImage && !video && <Picture {...titleImage} sizes={[{ width: '100vw' }]} />}
            {video && mobileVideo && (
              <>
                <video
                  ref={desktopVideoRef}
                  autoPlay
                  muted
                  playsInline
                  loop
                  className="campaignStage__media--desktop"
                  aria-label={`Animation ${data.headline ? `"${data.headline}"` : ''}`}
                >
                  <source src={video} type="video/mp4" />
                </video>
                <video
                  ref={mobileVideoRef}
                  autoPlay
                  muted
                  playsInline
                  loop
                  className="campaignStage__media--mobile"
                  aria-label={`Animation ${data.headline ? `"${data.headline}"` : ''}`}
                >
                  <source src={mobileVideo} type="video/mp4" />
                </video>
                <div className="campaignStage__media--overlay" />
              </>
            )}
          </div>
        )}
        {data?.linkEntry?.[0]?.uri && (
          <div className="campaignStage__link-container">
            <div className="campaignStage__link-inner-container">
              <Link
                href={{
                  pathname: '/universal',
                  query: { uri: data.linkEntry[0].uri }
                }}
                as={data.linkEntry[0].uri}
                className="campaignStage__link"
                title={data.linkLabel ?? ''}
                aria-label={`${t('openPage')} "${data.linkLabel}"`}
              >
                {data.linkLabel && (
                  <span className="campaignStage__inner-link">{data.linkLabel}</span>
                )}
                <span className="campaignStage__stretched-link" />
              </Link>
              <div className="campaignStage__arrow" aria-hidden="true">
                <ArrowRight role="img" aria-hidden="true" />
              </div>
            </div>
          </div>
        )}
        {video && mobileVideo && (
          <button
            type="button"
            className="campaignStage__video-control"
            onClick={handlePlay}
            aria-label={isPlaying ? t('pause') : t('play')}
            aria-pressed={!isPlaying}
          >
            {isPlaying ? (
              <>
                <svg
                  aria-hidden="true"
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  focusable="false"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="5" y="4" width="2.80253" height="13.0121" fill="currentColor" />
                  <rect x="13" y="4" width="2.80253" height="13.0121" fill="currentColor" />
                </svg>
                <span className="sr-only">{t('pause')}</span>
              </>
            ) : (
              <>
                <svg
                  aria-hidden="true"
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  focusable="false"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M16 10.5L6 16V5L16 10.5Z" fill="currentColor" />
                </svg>
                <span className="sr-only">{t('play')}</span>
              </>
            )}
          </button>
        )}
        <Container>
          <div className="campaignStage__headline">
            {data?.headline && (
              <Headline tag="h1" primary disableHyphens>
                {data.headline}
              </Headline>
            )}
          </div>
          {data?.showAnchorLinks && (
            <div className="campaignStage__anchor-nav">
              {anchorNav.length > 0 &&
                anchorNav.map(item => {
                  if (!item.title || !item.id) {
                    return null;
                  }
                  return (
                    <button
                      type="button"
                      className="campaignStage__anchor-nav-link"
                      onClick={() => smoothScrollToElementById(item.id)}
                      key={item.title}
                      aria-label={`${t('jumpTo')} ${item.title}`}
                    >
                      <FilterTag inverted light>
                        {item.title}
                      </FilterTag>
                    </button>
                  );
                })}
            </div>
          )}
        </Container>
      </div>
    </StyledShapedStage>
  );
};

ShapedStage.propTypes = {
  titleImage: PropTypes.shape({
    imageFormat: PropTypes.oneOf(['portrait', 'landscape']),
    credits: PropTypes.string
  }),
  anchorNav: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired
    })
  ),
  data: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    showAnchorLinks: PropTypes.bool,
    linkLabel: PropTypes.string,
    linkEntry: PropTypes.arrayOf(
      PropTypes.shape({
        uri: PropTypes.string.isRequired
      })
    ),
    mobileVideo: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired
      })
    ),
    video: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired
      })
    )
  })
};

const mapStateToProps = ({ app }) => ({
  titleImage: app.pageData.titleImage
});

export default connect(mapStateToProps)(ShapedStage);
